import React, { ReactNode } from 'react';
import {
  Paper,
  Typography,
  Box,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemButton,
  SvgIconProps
} from '@mui/material';
import { Link } from 'react-router-dom';

interface DashboardCardProps {
  title: string;
  icon: React.ReactElement<SvgIconProps>;
  items?: {
    id: string;
    text: string;
    path: string;
    icon?: React.ReactElement;
  }[];
  children?: ReactNode;
  alertContent?: ReactNode; // Neues Prop für den Alert-Inhalt
}

const DashboardCard: React.FC<DashboardCardProps> = ({ 
  title, 
  icon, 
  items,
  children,
  alertContent // Alert-Inhalt
}) => {
  return (
    <Paper
      elevation={2}
      sx={{
        borderRadius: 2,
        overflow: 'hidden',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 6px 12px rgba(0,0,0,0.15)'
        }
      }}
    >
      {/* Card Header */}
      <Box
        sx={{
          bgcolor: 'primary.main',
          color: 'white',
          p: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5
        }}
      >
        {React.cloneElement(icon, { 
          fontSize: 'medium',
          sx: { color: 'white' }
        })}
        <Typography variant="h6" component="h2" sx={{ fontWeight: 500 }}>
          {title}
        </Typography>
      </Box>
      
      <Divider />
      
      {/* Alert direkt nach der Überschrift und vor den Items */}
      {alertContent && (
        <Box sx={{ px: 0, py: 0 }}>
          {alertContent}
          <Divider />
        </Box>
      )}
      
      {/* Card Content */}
      <Box sx={{ p: 0, flexGrow: 1 }}>
        {items && items.length > 0 && (
          <List sx={{ p: 0 }}>
            {items.map((item) => (
              <ListItem key={item.id} disablePadding divider>
                <ListItemButton
                  component={Link}
                  to={item.path}
                  sx={{
                    py: 1.5,
                    '&:hover': {
                      bgcolor: 'rgba(25, 118, 210, 0.08)'
                    }
                  }}
                >
                  {item.icon && (
                    <ListItemIcon sx={{ color: 'primary.main', minWidth: 40 }}>
                      {item.icon}
                    </ListItemIcon>
                  )}
                  <ListItemText 
                    primary={item.text}
                    primaryTypographyProps={{
                      sx: {
                        fontWeight: 400,
                        color: 'text.primary'
                      }
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}
        
        {children && (
          <Box sx={{ p: 2 }}>
            {children}
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default DashboardCard;